import { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';

export default function AnimatedShield({ isActive = false, size = 'md' }) {
  const [pulse, setPulse] = useState(false);
  const [color, setColor] = useState('idle'); // idle, working, success

  useEffect(() => {
    if (isActive) {
      // Simulate working state
      setColor('working');
      setPulse(true);
      
      // After 2-3 seconds, show success
      const timer = setTimeout(() => {
        setColor('success');
        setPulse(false);
        
        // Then return to idle
        setTimeout(() => {
          setColor('idle');
        }, 2000);
      }, 2500);
      
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  const getColorClasses = () => {
    switch (color) {
      case 'working':
        return {
          bg: 'bg-yellow-500/20',
          border: 'border-yellow-500/50',
          icon: 'text-yellow-400',
          glow: 'shadow-yellow-500/30',
          ring: 'ring-yellow-500/30'
        };
      case 'success':
        return {
          bg: 'bg-[#00FF94]/20',
          border: 'border-[#00FF94]/50',
          icon: 'text-[#00FF94]',
          glow: 'shadow-[#00FF94]/30',
          ring: 'ring-[#00FF94]/30'
        };
      default:
        return {
          bg: 'bg-[#00FF94]/10',
          border: 'border-[#00FF94]/20',
          icon: 'text-[#00FF94]',
          glow: 'shadow-[#00FF94]/20',
          ring: 'ring-[#00FF94]/20'
        };
    }
  };

  const colors = getColorClasses();

  return (
    <div className="relative" data-testid="animated-shield">
      {/* Outer pulse ring */}
      {pulse && (
        <div 
          className={`absolute inset-0 ${sizeClasses[size]} rounded-xl ${colors.bg} animate-ping`}
          style={{ animationDuration: '1.5s' }}
        />
      )}
      
      {/* Rotating ring (when working) */}
      {color === 'working' && (
        <div 
          className={`absolute inset-0 ${sizeClasses[size]} rounded-xl border-2 border-transparent`}
          style={{
            background: `conic-gradient(from 0deg, transparent, rgba(234, 179, 8, 0.5), transparent)`,
            animation: 'spin 2s linear infinite'
          }}
        />
      )}
      
      {/* Main shield container */}
      <div 
        className={`
          relative ${sizeClasses[size]} rounded-xl ${colors.bg} ${colors.border} 
          border flex items-center justify-center transition-all duration-500
          ${pulse ? 'scale-110' : 'scale-100'}
          ${color === 'success' ? 'shadow-lg ' + colors.glow : ''}
        `}
      >
        <Shield 
          className={`${iconSizes[size]} ${colors.icon} transition-colors duration-300 ${color === 'working' ? 'animate-pulse' : ''}`}
        />
        
        {/* Success checkmark overlay */}
        {color === 'success' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg 
              className="w-full h-full text-[#00FF94] animate-[scale-in_0.3s_ease-out]" 
              viewBox="0 0 24 24"
              style={{ padding: '30%' }}
            >
              <path 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
                style={{
                  strokeDasharray: 24,
                  strokeDashoffset: 0,
                  animation: 'draw-check 0.5s ease-out forwards'
                }}
              />
            </svg>
          </div>
        )}
      </div>
      
      {/* Status text (optional) */}
      {size === 'xl' && (
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
          <span className={`text-xs font-mono ${colors.icon}`}>
            {color === 'working' ? 'PROCESSING...' : color === 'success' ? 'COMPLETE' : 'PROTECTED'}
          </span>
        </div>
      )}
    </div>
  );
}

// Inline keyframes for the checkmark animation
const style = document.createElement('style');
style.textContent = `
  @keyframes draw-check {
    0% { stroke-dashoffset: 24; }
    100% { stroke-dashoffset: 0; }
  }
  @keyframes scale-in {
    0% { transform: scale(0); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}
